import { afterNextRender, Component, inject, Injector, input, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { OfferingServiceAndRequest } from '../../../models/offering';
import { MercuryFormService } from '../../../services/mercury-form.service';
import { BUTTON_TEXTS } from '../../../constants/buttons.constants';
import { RdsButtonModule, RdsProgressSpinnerComponent } from '@rds/angular-components';
import { UiService } from '../../../services/ui.service';
import { finalize } from 'rxjs';
import { MERCURY_FORM_TEXTS } from '../../../constants/mercury-form.constants';
import { FormConfigDetails, FormConfigResponse } from '../../../models/mercury-form';
import { FaroActionDirective } from '../../../directives/faro-action.directive';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { FILM_DYNAMIC_FORM_RENDERING_FLAG } from '../mercury-form/mercury-form.component';
import { SNOW_RENDERER_FLAG } from '../catalog-form/catalog-form.component';
import { FulfillmentUtils } from '../../../util/fulfillment-utils';
import { CATALOG_FORM_ROUTE, CATALOG_FORM_TEXTS } from '../../../constants/catalog-form.constants';

// INEXP-2497: routes ServiceNow-channel offerings to the in-app catalog form.
// Off → every row keeps the Mercury / external-link path it had before.
export const SNOW_GET_STARTED_FLAG = 'focus_inexp_2497_snow_get_started';

// History-state key for the card whose Get Started opened the catalog form,
// so Back from that form can put focus back on the same button.
export const RETURN_FOCUS_STATE_KEY = 'inexpGetStartedId';

// Fallback id prefix when Storyblok gives no _uid, so two blocks on one page
// never share button ids.
let nextInstanceId = 0;

@Component({
  selector: 'inexp-offering-services-and-requests',
  imports: [RdsButtonModule, RdsProgressSpinnerComponent, FaroActionDirective],
  templateUrl: './offering-services-and-requests.component.html',
  styleUrl: './offering-services-and-requests.component.scss',
})
export class OfferingServicesAndRequestsComponent implements OnInit {
  _uid = input<string>();
  component = input<string>();
  section = input<string>();
  router = inject(Router);
  servicesAndRequests = input<OfferingServiceAndRequest[]>();

  uniqueTypes: string[] = [];
  formIdMap: Record<string, string> = {};
  // INEXP-2497: offeringId → formId for the non-Mercury Platform values FILM
  // writes into fulfillmentChannel. ServiceNow rows carry the catalog sysId,
  // URL rows carry the link. Filled regardless of flags — the flags are read
  // at click time so a late Unleash start can't misroute a row.
  readonly catalogSysIdMap = signal<Record<string, string>>({});
  readonly formUrlMap = signal<Record<string, string>>({});
  // INEXP-2375: pre-warmed per-offering result of the form-availability flag.
  // Populated after getFormConfigDetails resolves so navigateToMercury can
  // read it synchronously and keep window.open inside the click gesture
  // (async awaits between click and open trigger popup blockers).
  readonly formEnabledMap = signal<Record<string, boolean>>({});
  isFormReady = false;
  isLoading = false;
  errorMessage: string | null = null;
  isNavigating = false;

  readonly BUTTONS = BUTTON_TEXTS;
  readonly FORM_TEXT = MERCURY_FORM_TEXTS;
  readonly NEW_TAB_SR = CATALOG_FORM_TEXTS.NEW_TAB_SR;
  private readonly _mercuryFormService = inject(MercuryFormService);
  private readonly _uiService = inject(UiService);
  private readonly _featureFlagService = inject(FeatureFlagService);
  private readonly _location = inject(Location);
  private readonly _injector = inject(Injector);
  private _returnFocusId: string | null = null;
  private readonly _instanceId = `offering-${nextInstanceId++}`;

  ngOnInit() {
    const services = this.servicesAndRequests() || [];
    this._returnFocusId = this._readReturnFocusId();

    this.uniqueTypes = Array.from(
      new Set(services.map((item) => item.type || ''))
    );

    if (services.length > 0) {
      const primaryOffering = services[0];
      this.loadFormConfig(primaryOffering);
    }
  }

  loadFormConfig(item: OfferingServiceAndRequest) {
    if (!item?.id) {
      this._restoreGetStartedFocus();
      return;
    }

    const offeringId = item.id;
    this.isLoading = true;
    this.errorMessage = null;

    this._mercuryFormService
      .getFormConfigDetails(offeringId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          // Buttons are disabled while loading, so focus can only land now.
          this._restoreGetStartedFocus();
        }),
      )
      .subscribe({
        next: (res: FormConfigResponse) => {
          const formList = res?.offeringFormDetails;

          if (!Array.isArray(formList)) {
            this.isFormReady = false;
            return;
          }

          // Response carries every offering on the page; map them all so each card resolves.
          const catalogSysIds: Record<string, string> = {};
          const formUrls: Record<string, string> = {};
          for (const f of formList) {
            if (!f?.formId || f?.offeringId == null) continue;
            const key = String(f.offeringId);
            switch (FulfillmentUtils.resolveChannel(f.fulfillmentChannel)) {
              case 'mercury':
                this.formIdMap[key] = f.formId;
                break;
              case 'servicenow':
                catalogSysIds[key] = f.formId;
                break;
              case 'url':
                formUrls[key] = f.formId;
                break;
              default:
                // Unknown channel → no mapping; Get Started uses destinationValue.
                break;
            }
          }
          this.catalogSysIdMap.set(catalogSysIds);
          this.formUrlMap.set(formUrls);

          // Hand the full list to the mercury-form page so it can look up its
          // own entry by formId without re-hitting /formConfigDetails.
          this._mercuryFormService.setFormConfigs(formList);

          this.isFormReady =
            Object.keys(this.formIdMap).length > 0 ||
            Object.keys(catalogSysIds).length > 0 ||
            Object.keys(formUrls).length > 0;

          // Triggering offering listed but with empty formId → upstream config bug; surface it.
          const primaryMatch = formList.find(
            (f: FormConfigDetails) => String(f.offeringId) === String(offeringId)
          );
          if (primaryMatch && !primaryMatch.formId) {
            this.errorMessage = MERCURY_FORM_TEXTS.NO_FORM_FOUND;
            this._uiService.showErrorToast(
              MERCURY_FORM_TEXTS.ERROR,
              MERCURY_FORM_TEXTS.NO_FORM_FOUND
            );
          }

          void this._prewarmFormEnabledFlags(formList);
        },

        error: () => {
          this.isFormReady = false;

          // Show error ONLY if there is no fallback external link on the offering
          if (!item.destinationValue) {
            this._uiService.showErrorToast(
              MERCURY_FORM_TEXTS.ERROR,
              MERCURY_FORM_TEXTS.FORM_CONFIG_ERROR
            );
          }
        },
      });
  }

  getStarted(item: OfferingServiceAndRequest): void {
    // ServiceNow catalog item → in-app catalog form. With either flag off the
    // row is not in formIdMap, so navigateToMercury falls to destinationValue.
    const sysId = this.catalogSysIdMap()[item.id];
    if (sysId && this._isServiceNowRoutingEnabled()) {
      this._rememberGetStarted(item);
      this.router
        .navigate([CATALOG_FORM_ROUTE, sysId], { state: { offeringId: item.id } })
        .catch(() => this._showFormLoadError());
      return;
    }

    // URL platform → new tab, synchronous inside the click for popup blockers.
    // A non-http(s) value falls through to destinationValue instead.
    const url = this.formUrlMap()[item.id];
    if (url && FulfillmentUtils.isSafeExternalUrl(url)) {
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        this._showFormLoadError();
      }
      return;
    }

    this.navigateToMercury(item);
  }

  navigateToMercury(item: OfferingServiceAndRequest): void {
    const formId = this.formIdMap[item.id];

    // 1. Mercury Form Navigation — same-tab router navigation to avoid
    // CDN path-specific rewrites on /mercury-form/* deep links.
    if (formId) {
      // INEXP-2375: fall open (default true) when the pre-warm did not run
      // for this offering — matches the mercury-form fail-closed-to-render
      // guard so behaviour stays consistent if the flag context is missing.
      const enabled = this.formEnabledMap()[item.id] ?? true;
      if (!enabled) {
        this._handleFormDisabled(item.destinationValue);
        return;
      }

      // Flag ON → navigate directly to the mercury form.
      // Thread destinationValue via navigation state so mercury-form can open
      // the origin URL if the per-offering flag is OFF — state keeps the URL
      // clean (no ?redirect= visible in the address bar). offeringId rides
      // along the same way, so mercury-form picks this offering's config when
      // several offerings share the formId; history.state survives a reload.
      const navExtras = {
        state: {
          offeringId: item.id,
          ...(item.destinationValue ? { redirect: item.destinationValue } : {}),
        },
      };
      this.router
        .navigate(['/mercury-form', formId], navExtras)
        .catch(() => this._showFormLoadError());
      return;
    }

    // 2. External Link Navigation (Non-Mercury Form)
    if (item.destinationValue) {
      // Synchronous execution prevents browser popup blockers
      const win = window.open(
        item.destinationValue,
        '_blank',
        'noopener,noreferrer'
      );
      if (!win) {
        this._showFormLoadError();
      }
      return;
    }

    // 3. Navigation Error (Neither internal form nor external link exists)
    this._uiService.showErrorToast(
      MERCURY_FORM_TEXTS.ERROR,
      MERCURY_FORM_TEXTS.NAVIGATION_ERROR
    );
  }

  getStartedId(index: number): string {
    return `get-started-${this._uid() ?? this._instanceId}-${index}`;
  }

  // URL-platform rows open a new tab; the button text alone doesn't say so.
  opensInNewTab(item: OfferingServiceAndRequest): boolean {
    return FulfillmentUtils.isSafeExternalUrl(this.formUrlMap()[item.id]);
  }

  // Stamp the clicked button's id on the offering page's own history entry;
  // Back from the catalog form pops this entry and its state with it. The id
  // includes _uid, so other blocks on the same page ignore it.
  private _rememberGetStarted(item: OfferingServiceAndRequest): void {
    const index = (this.servicesAndRequests() ?? []).indexOf(item);
    if (index < 0) return;
    this._location.replaceState(this._location.path(true), '', {
      ...this._historyState(),
      [RETURN_FOCUS_STATE_KEY]: this.getStartedId(index),
    });
  }

  private _readReturnFocusId(): string | null {
    const id = this._historyState()[RETURN_FOCUS_STATE_KEY];
    return typeof id === 'string' && id.startsWith(`get-started-${this._uid() ?? this._instanceId}-`)
      ? id
      : null;
  }

  private _restoreGetStartedFocus(): void {
    const id = this._returnFocusId;
    if (id === null) return;
    this._returnFocusId = null;

    // Clear it so a later reload or Back to this page doesn't steal focus again.
    const state = { ...this._historyState() };
    delete state[RETURN_FOCUS_STATE_KEY];
    this._location.replaceState(this._location.path(true), '', state);

    afterNextRender(() => document.getElementById(id)?.focus(), {
      injector: this._injector,
    });
  }

  private _historyState(): Record<string, unknown> {
    return (this._location.getState() as Record<string, unknown> | null) ?? {};
  }

  // Both flags are needed: without the renderer flag the catalog-form page
  // renders nothing, so routing there would strand the user.
  private _isServiceNowRoutingEnabled(): boolean {
    return (
      this._featureFlagService.isEnabled(SNOW_GET_STARTED_FLAG) &&
      this._featureFlagService.isEnabled(SNOW_RENDERER_FLAG)
    );
  }

  private _showFormLoadError(): void {
    this._uiService.showErrorToast(
      MERCURY_FORM_TEXTS.FORM_LOAD_ERROR_TITLE,
      MERCURY_FORM_TEXTS.FORM_LOAD_ERROR
    );
  }

  // Flag OFF path: open the origin URL in a new tab (window.open MUST stay
  // synchronous inside the click handler for popup blockers), then show a toast.
  private _handleFormDisabled(destinationValue: string | undefined): void {
    if (destinationValue) {
      const win = window.open(destinationValue, '_blank', 'noopener,noreferrer');
      if (!win) {
        this._uiService.showErrorToast(MERCURY_FORM_TEXTS.ERROR, MERCURY_FORM_TEXTS.POPUP_BLOCKED);
        return;
      }
    }
    this._uiService.showErrorToast(MERCURY_FORM_TEXTS.FORM_NOT_FOUND_TITLE, MERCURY_FORM_TEXTS.FORM_NOT_FOUND);
  }

  // Sequentially evaluate the per-offering flag so we can decide in
  // navigateToMercury without any awaits. Sequential (not Promise.all)
  // because FeatureFlagService.isEnabledWithContext mutates a shared
  // Unleash context and would race under parallel calls.
  private async _prewarmFormEnabledFlags(
    formList: FormConfigDetails[]
  ): Promise<void> {
    for (const config of formList) {
      if (!config?.offeringId || !config?.fulfillmentType) continue;
      const key = String(config.offeringId);
      try {
        const enabled = await this._featureFlagService.isEnabledWithContext(
          FILM_DYNAMIC_FORM_RENDERING_FLAG,
          {
            offeringId: key,
            offeringType: config.fulfillmentType,
          }
        );
        this.formEnabledMap.update((m) => ({ ...m, [key]: enabled }));
      } catch {
        // Fail open — keep the entry unset so navigateToMercury's `?? true`
        // fallback allows the form through rather than trapping users.
      }
    }
  }
}
